import { Alert, Box, CircularProgress } from '@mui/material';
import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import useAdminUserExport from '../../../../hooks/admin/useAdminUserExport';
import useAdminUserSWR from '../../../../hooks/admin/useAdminUserSWR';
import useAddToCompanyDialog from '../../../../hooks/useAddToCompanyDialog';
import useProfileEditorDialog from '../../../../hooks/useProfileEditorDialog';
import {
    AdminUser,
    AdminUsersHeadCellContent,
} from '../../../../models/Admin/AdminUsers';
import { ADMIN_USERS_HEAD_CELLS } from '../../../../utils/constans/adminUsersHead';
import { AdminUsersTableRow } from '../../../admin/users/AdminUsersTableRow';
import PrimaryBtn from '../../../shared/Buttons/PrimaryButton';
import { SWRTable } from '../../../shared/Table/SWRTable';

export const AdminUsers = () => {
    const { t } = useTranslation('admin');
    const [page, setPage] = React.useState(1);
    const [rowsPerPage, setRowsPerPage] = React.useState(25);
    const [search, setSearch] = React.useState('');
    const [userList, setUserList] = React.useState(null);
    const { users, isLoading, forceRefresh } = useAdminUserSWR({
        search: search,
        page: page,
        size: rowsPerPage,
    });
    const [isExporting, setIsExporting] = React.useState(false);
    const [exportError, setExportError] = React.useState(false);
    const { exportUsers } = useAdminUserExport();

    const handleExport = () => {
        setIsExporting(true);
        setExportError(false);
        exportUsers()
            .catch(() => setExportError(true))
            .finally(() => setIsExporting(false));
    };
    const profileDialog = useProfileEditorDialog();
    const addToCompanyDialog = useAddToCompanyDialog();

    useEffect(() => {
        if (users) {
            setUserList(users);
        }
    }, [users, isLoading]);

    const defaultPaginateOptions = {
        lazyPaginate: true,
        size: rowsPerPage,
        page: page - 1,
        search: search,
    };

    const tableSettings = {
        tableHeadSettings: {
            translateNs: 'admin',
            translatePrefix: 'USERS.TABLE_HEAD',
            selectable: false,
            headContent: ADMIN_USERS_HEAD_CELLS,
        },
        tableToolbarSettings: {
            translateNs: 'admin',
            translatePrefix: 'USERS.TABLE_TOOLBAR',
        },
    };

    const handleChange = (size: number, page: number, filter: string) => {
        setSearch(filter);
        setPage(page + 1);
        setRowsPerPage(size);
    };

    useEffect(() => {
        if (profileDialog.profileDialogClosedWithSuccess) {
            forceRefresh().then((users) => {
                setUserList(users);
            });
            profileDialog.setProfileDialogClosedWithSuccess(false);
        }
    }, [profileDialog.profileDialogClosedWithSuccess]);

    useEffect(() => {
        if (addToCompanyDialog.addToCompanyDialogState.successfulSend) {
            forceRefresh().then((users) => {
                setUserList(users);
            });
            addToCompanyDialog.setSuccess(false);
        }
    }, [addToCompanyDialog.addToCompanyDialogState.successfulSend]);

    return (
        <>
            {exportError && (
                <Alert
                    severity="error"
                    onClose={() => setExportError(false)}
                    sx={{ mb: 2 }}
                >
                    {t('USERS.EXPORT.ERROR')}
                </Alert>
            )}
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
                <PrimaryBtn
                    variant="contained"
                    onClick={handleExport}
                    disabled={isExporting}
                    startIcon={
                        isExporting ? (
                            <CircularProgress size={16} color="inherit" />
                        ) : null
                    }
                >
                    {t('USERS.EXPORT.BUTTON')}
                </PrimaryBtn>
            </Box>
            <SWRTable<AdminUser, AdminUsersHeadCellContent>
                listItems={userList ? userList.users : []}
                totalNumber={userList ? userList.max_item : 0}
                component={AdminUsersTableRow}
                handleChange={handleChange}
                paginateOptions={defaultPaginateOptions}
                tableSettings={tableSettings}
                forceRefresh={forceRefresh}
                isLoading={isLoading}
            />
        </>
    );
};
